import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LocationAutocomplete } from '@/components/ui/location-autocomplete';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Calendar, Sun, Cloud, DollarSign, Users, TrendingUp, Sparkles, AlertTriangle } from 'lucide-react';
import { SubscriptionGate } from '@/components/SubscriptionGate';
import { useSubscription } from '@/hooks/useSubscription';

interface SeasonalData {
  destination: string;
  country: string;
  bestMonths: {
    month: string;
    score: number;
    reasons: string[];
  }[];
  avoidMonths: {
    month: string;
    reasons: string[];
  }[];
  peakSeason: {
    months: string;
    description: string;
    crowdLevel: 'low' | 'medium' | 'high';
    priceLevel: 'budget' | 'moderate' | 'expensive';
  };
  shoulderSeason: {
    months: string;
    description: string;
    benefits: string[];
  };
  lowSeason: {
    months: string;
    description: string;
    benefits: string[];
    warnings: string[];
  };
}

export function SeasonalRecommendations() {
  const [destination, setDestination] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [seasonalData, setSeasonalData] = useState<SeasonalData | null>(null);
  const { hasFeature } = useSubscription();

  const generateSeasonalData = (dest: string, ctry: string): SeasonalData => {
    // This would typically call an API, but for now we'll generate based on location
    const isNorthernHemisphere = !['australia', 'new zealand', 'argentina', 'chile', 'south africa'].some(
      c => ctry.toLowerCase().includes(c)
    );

    const isTropical = ['thailand', 'bali', 'indonesia', 'philippines', 'vietnam', 'malaysia', 'singapore'].some(
      c => dest.toLowerCase().includes(c) || ctry.toLowerCase().includes(c)
    );

    if (isTropical) {
      return {
        destination: dest,
        country: ctry,
        bestMonths: [
          { month: 'November', score: 95, reasons: ['Dry season begins', 'Perfect weather', 'Less crowded than peak'] },
          { month: 'December', score: 92, reasons: ['Dry and cool', 'Festive atmosphere', 'Clear skies'] },
          { month: 'January', score: 94, reasons: ['Dry season peak', 'Best beach weather', 'Ideal temperatures'] },
          { month: 'February', score: 93, reasons: ['Continued dry season', 'Warm but not too hot', 'Great visibility'] }
        ],
        avoidMonths: [
          { month: 'June-September', reasons: ['Monsoon season', 'Heavy rainfall', 'High humidity', 'Potential flooding'] }
        ],
        peakSeason: {
          months: 'December - February',
          description: 'Dry season with perfect weather conditions',
          crowdLevel: 'high',
          priceLevel: 'expensive'
        },
        shoulderSeason: {
          months: 'March-May & October-November',
          description: 'Transitional period with good weather and fewer crowds',
          benefits: ['Lower prices (20-30% discount)', 'Less crowded attractions', 'Still good weather', 'Better hotel availability']
        },
        lowSeason: {
          months: 'June - September',
          description: 'Rainy monsoon season',
          benefits: ['Lowest prices (40-50% discount)', 'Empty beaches', 'Authentic local experience', 'Lush green landscapes'],
          warnings: ['Frequent rain', 'Some attractions may close', 'Limited water activities', 'High humidity']
        }
      };
    }

    // Default temperate destination (Northern Hemisphere)
    return {
      destination: dest,
      country: ctry,
      bestMonths: isNorthernHemisphere ? [
        { month: 'May', score: 92, reasons: ['Spring weather', 'Blooming flowers', 'Moderate temperatures'] },
        { month: 'June', score: 94, reasons: ['Early summer', 'Long daylight hours', 'Pleasant temperatures'] },
        { month: 'September', score: 93, reasons: ['Early fall', 'Comfortable weather', 'Fewer tourists'] },
        { month: 'October', score: 90, reasons: ['Fall colors', 'Cool weather', 'Harvest season'] }
      ] : [
        { month: 'November', score: 92, reasons: ['Spring weather', 'Warm temperatures', 'Outdoor activities'] },
        { month: 'December', score: 94, reasons: ['Early summer', 'Beach season starts', 'Long days'] },
        { month: 'March', score: 93, reasons: ['Early fall', 'Still warm', 'Less crowded'] },
        { month: 'April', score: 90, reasons: ['Autumn colors', 'Cooler weather', 'Wine season'] }
      ],
      avoidMonths: isNorthernHemisphere ? [
        { month: 'January-February', reasons: ['Cold weather', 'Short days', 'Possible snow/ice'] }
      ] : [
        { month: 'June-August', reasons: ['Winter season', 'Cold weather', 'Some attractions closed'] }
      ],
      peakSeason: {
        months: isNorthernHemisphere ? 'June - August' : 'December - February',
        description: 'Summer season with best weather',
        crowdLevel: 'high',
        priceLevel: 'expensive'
      },
      shoulderSeason: {
        months: isNorthernHemisphere ? 'April-May & September-October' : 'October-November & March-April',
        description: 'Spring and fall with pleasant weather',
        benefits: ['20-30% lower prices', 'Fewer crowds', 'Good weather', 'Better service quality']
      },
      lowSeason: {
        months: isNorthernHemisphere ? 'November - March' : 'May - September',
        description: 'Winter season with cold weather',
        benefits: ['Lowest prices (up to 50% off)', 'No crowds', 'Authentic experience', 'Cozy atmosphere'],
        warnings: ['Cold temperatures', 'Shorter days', 'Some attractions closed', 'Weather-dependent activities']
      }
    };
  };

  const handleSearch = async () => {
    if (!destination.trim()) return;

    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const data = generateSeasonalData(destination, country);
    setSeasonalData(data);
    setLoading(false);
  };

  const getCrowdLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-muted';
    }
  };

  const getPriceLevelColor = (level: string) => {
    switch (level) {
      case 'budget': return 'bg-green-500';
      case 'moderate': return 'bg-yellow-500';
      case 'expensive': return 'bg-red-500';
      default: return 'bg-muted';
    }
  };

  if (!hasFeature('seasonal_recommendations')) {
    return (
      <SubscriptionGate feature="seasonal_recommendations" requiredTier="first_class">
        <div />
      </SubscriptionGate>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-card/60 to-card/30 backdrop-blur-sm border-border/40">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calendar className="h-6 w-6 text-primary" />
          <CardTitle className="flex items-center gap-2">
            Best Time to Visit
            <Badge variant="secondary" className="ml-2">First Class</Badge>
          </CardTitle>
        </div>
        <CardDescription>
          Get detailed seasonal recommendations for your destination
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex gap-2">
          <div className="flex-1">
            <LocationAutocomplete
              value={destination}
              onChange={setDestination}
              onLocationSelect={(location) => {
                setDestination(location.name);
                setCountry(location.country);
              }}
              placeholder="Enter destination..."
              label=""
            />
          </div>
          <Button onClick={handleSearch} disabled={loading || !destination.trim()} size="lg">
            {loading ? <LoadingSpinner size="sm" /> : <Sparkles className="h-5 w-5" />}
            Analyze
          </Button>
        </div>

        {loading && (
          <div className="py-12">
            <LoadingSpinner size="lg" text="Analyzing seasonal patterns..." />
          </div>
        )}

        {seasonalData && !loading && (
          <div className="space-y-6 animate-fade-in">
            {/* Best Months */}
            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center gap-2 mb-4">
                <Sun className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Best Months to Visit</h3>
              </div>
              <div className="grid gap-3">
                {seasonalData.bestMonths.map((month, idx) => (
                  <div key={idx} className="bg-background/80 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{month.month}</span>
                      <Badge variant="secondary">{month.score}/100</Badge>
                    </div>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {month.reasons.map((reason, ridx) => (
                        <li key={ridx} className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Peak Season */}
            <div className="bg-card rounded-lg p-4 border">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-orange-500" />
                <h3 className="font-semibold">Peak Season</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{seasonalData.peakSeason.months}</span>
                </div>
                <p className="text-sm text-muted-foreground">{seasonalData.peakSeason.description}</p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Crowds:</span>
                    <Badge className={getCrowdLevelColor(seasonalData.peakSeason.crowdLevel)}>
                      {seasonalData.peakSeason.crowdLevel}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Prices:</span>
                    <Badge className={getPriceLevelColor(seasonalData.peakSeason.priceLevel)}>
                      {seasonalData.peakSeason.priceLevel}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Shoulder Season */}
            <div className="bg-card rounded-lg p-4 border">
              <div className="flex items-center gap-2 mb-3">
                <Cloud className="h-5 w-5 text-blue-500" />
                <h3 className="font-semibold">Shoulder Season (Recommended)</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{seasonalData.shoulderSeason.months}</span>
                </div>
                <p className="text-sm text-muted-foreground">{seasonalData.shoulderSeason.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {seasonalData.shoulderSeason.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-green-500">✓</span>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Low Season */}
            <div className="bg-card rounded-lg p-4 border">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="h-5 w-5 text-green-500" />
                <h3 className="font-semibold">Low Season (Budget-Friendly)</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{seasonalData.lowSeason.months}</span>
                </div>
                <p className="text-sm text-muted-foreground">{seasonalData.lowSeason.description}</p>
                
                <div>
                  <p className="text-sm font-medium mb-2">Benefits:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {seasonalData.lowSeason.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500">✓</span>
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {seasonalData.lowSeason.warnings.length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm font-medium">Consider:</span>
                    </div>
                    <ul className="text-sm space-y-1">
                      {seasonalData.lowSeason.warnings.map((warning, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-yellow-500">•</span>
                          <span>{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Avoid Months */}
            {seasonalData.avoidMonths.length > 0 && (
              <div className="bg-destructive/5 rounded-lg p-4 border border-destructive/20">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  <h3 className="font-semibold">Months to Avoid</h3>
                </div>
                {seasonalData.avoidMonths.map((month, idx) => (
                  <div key={idx} className="space-y-2">
                    <span className="font-medium">{month.month}</span>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {month.reasons.map((reason, ridx) => (
                        <li key={ridx} className="flex items-start gap-2">
                          <span className="text-destructive">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
